// The presentation workspace: outline navigator | slides-markdown editor |
// structure preview, with a per-slide AI "adjust" control. Generation runs on the
// server (kicked off from the project chooser); this page polls status, lets you
// edit the canonical `slides_markdown` with debounced autosave, and previews the
// parsed slide structure. Mirrors the report page, minus the LaTeX/PDF pieces.
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  generatePresentation,
  getPresentation,
  rewritePresentationSlide,
  savePresentation,
  type PresentationRead,
  type PresentationRewriteResult,
} from "@/api/presentation";
import { Alert, Button, Spinner } from "@/components/ui";
import { MathText } from "@/components/math-text";
import { MarkdownLite } from "../markdown-lite";
import { SlideEditorPanel } from "./slide-editor-panel";
import type { CodeMirrorHandle } from "../report/codemirror-editor";

interface ParsedSlide {
  title: string;
  bullets: string[];
  notes: string[];
  figures: string[];
}

// Split on lines equal to `---` (the canonical slide separator); one chunk per
// slide, indices aligned with the server-derived outline.
function splitSlides(md: string): string[] {
  const out: string[] = [];
  let buf: string[] = [];
  for (const line of md.replace(/\r\n/g, "\n").split("\n")) {
    if (line.trim() === "---") {
      out.push(buf.join("\n"));
      buf = [];
    } else {
      buf.push(line);
    }
  }
  out.push(buf.join("\n"));
  return out;
}

function parseSlide(chunk: string): ParsedSlide {
  const slide: ParsedSlide = { title: "", bullets: [], notes: [], figures: [] };
  for (const line of chunk.split("\n")) {
    const fig = line.match(/^!\[[^\]]*\]\(([^)]*)\)\s*$/);
    const heading = line.match(/^#\s+(.*)$/);
    const bullet = line.match(/^-\s+(.*)$/);
    const note = line.match(/^>\s?(.*)$/);
    if (fig) slide.figures.push(fig[1].trim());
    else if (heading) slide.title = heading[1].trim();
    else if (bullet) slide.bullets.push(bullet[1].trim());
    else if (note) slide.notes.push(note[1]);
  }
  return slide;
}

function parseSlides(md: string): ParsedSlide[] {
  const chunks = splitSlides(md);
  // A single empty chunk means "no slides yet".
  if (chunks.length === 1 && chunks[0].trim() === "") return [];
  return chunks.map(parseSlide);
}

export function PresentationPage() {
  const { t } = useTranslation();
  const { projectId = "" } = useParams();
  const qc = useQueryClient();

  const editorRef = useRef<CodeMirrorHandle>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);

  const { data: pres, isLoading } = useQuery({
    queryKey: ["presentation", projectId],
    queryFn: () => getPresentation(projectId),
    retry: false,
    refetchInterval: (q) => {
      const d = q.state.data as PresentationRead | undefined;
      if (!d) return false;
      return d.status === "pending" || d.status === "running" ? 2500 : false;
    },
  });

  const generate = useMutation({
    mutationFn: () => generatePresentation(projectId),
    onSuccess: (doc) => {
      qc.setQueryData(["presentation", projectId], doc);
      qc.invalidateQueries({ queryKey: ["presentation", projectId] });
    },
  });

  const save = async (md: string) => {
    const doc = await savePresentation(projectId, md);
    qc.setQueryData(["presentation", projectId], doc);
  };

  // Flush the editor to the server first so the rewrite operates on the latest
  // text, then request a preview for the targeted slide.
  const previewRewrite = async (slideIndex: number, directive: string) => {
    const handle = editorRef.current;
    if (handle) {
      try {
        await save(handle.getDoc());
      } catch {
        /* best-effort flush; the rewrite still runs on the last saved text */
      }
    }
    return rewritePresentationSlide(projectId, {
      directive,
      slide_index: slideIndex,
      preview: true,
    });
  };

  const applyRewrite = async (slideIndex: number, directive: string) => {
    const res = await rewritePresentationSlide(projectId, {
      directive,
      slide_index: slideIndex,
      preview: false,
    });
    if (res.document) {
      qc.setQueryData(["presentation", projectId], res.document);
      editorRef.current?.setDoc(res.document.slides_markdown || "");
    }
    return res;
  };

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    cardRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const header = (
    <Link to={`/app/projects/${projectId}`} className="text-sm text-brand-600 hover:underline">
      {t("← Back to project")}
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!pres) {
    return (
      <div className="space-y-6">
        {header}
        <section className="card space-y-3 text-center">
          <h1 className="text-xl font-bold text-ink">{t("Presentation")}</h1>
          <p className="mx-auto max-w-prose text-sm text-ink-soft">
            {t("No presentation yet. Generate a slide deck from this project's sources, then edit it here.")}
          </p>
          {generate.isError && <Alert>{t("Could not start presentation generation.")}</Alert>}
          <Button onClick={() => generate.mutate()} loading={generate.isPending}>
            {t("Generate presentation")}
          </Button>
        </section>
      </div>
    );
  }

  if (pres.status === "pending" || pres.status === "running") {
    return (
      <div className="space-y-6">
        {header}
        <section className="card space-y-3">
          <div className="flex items-center gap-2">
            <Spinner className="h-4 w-4" />
            <span className="text-sm font-medium text-ink">
              {t("Generating your presentation…")}
            </span>
          </div>
          <p className="text-xs text-ink-faint">
            {t("This can take a few minutes. You can leave and come back.")}
          </p>
        </section>
      </div>
    );
  }

  if (pres.status === "failed") {
    return (
      <div className="space-y-6">
        {header}
        <Alert>{pres.error_message || t("Presentation generation failed.")}</Alert>
        <Button onClick={() => generate.mutate()} loading={generate.isPending}>
          {t("Try again")}
        </Button>
      </div>
    );
  }

  // Complete → the workspace.
  const slides = parseSlides(pres.slides_markdown || "");
  cardRefs.current.length = slides.length;

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        {header}
        <Button
          size="sm"
          variant="secondary"
          onClick={() => generate.mutate()}
          loading={generate.isPending}
        >
          {t("Regenerate")}
        </Button>
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[16rem_minmax(0,1fr)_minmax(0,1fr)]">
        {/* Outline navigator */}
        <div className="min-h-0 overflow-auto rounded-xl border border-black/10 bg-white p-2">
          <p className="px-1 py-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("Outline")}
          </p>
          {pres.outline.length === 0 ? (
            <p className="px-1 text-sm text-ink-faint">{t("No slides yet.")}</p>
          ) : (
            <ol className="space-y-0.5">
              {pres.outline.map((s) => (
                <li key={s.index}>
                  <button
                    type="button"
                    onClick={() => goToSlide(s.index)}
                    className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${
                      activeIndex === s.index
                        ? "bg-brand-100 text-brand-700"
                        : "text-ink-soft hover:bg-black/5"
                    }`}
                  >
                    <span className="mr-1.5 text-xs text-ink-faint">{s.index + 1}.</span>
                    {s.title || t("Untitled slide")}
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Editor */}
        <div className="min-h-0 rounded-xl border border-black/10 bg-white">
          <SlideEditorPanel
            documentKey={pres.id}
            initialValue={pres.slides_markdown || ""}
            onSave={save}
            editorRef={editorRef}
          />
        </div>

        {/* Structure preview */}
        <div className="min-h-0 overflow-auto rounded-xl border border-black/10 bg-white p-3">
          {slides.length === 0 ? (
            <p className="text-sm text-ink-faint">{t("No slides yet.")}</p>
          ) : (
            <div className="space-y-3">
              {slides.map((slide, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    cardRefs.current[i] = el;
                  }}
                >
                  <SlideCard
                    index={i}
                    slide={slide}
                    active={activeIndex === i}
                    onSelect={() => setActiveIndex(i)}
                    onPreview={previewRewrite}
                    onApply={applyRewrite}
                    onError={setError}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SlideCard({
  index,
  slide,
  active,
  onSelect,
  onPreview,
  onApply,
  onError,
}: {
  index: number;
  slide: ParsedSlide;
  active: boolean;
  onSelect: () => void;
  onPreview: (slideIndex: number, directive: string) => Promise<PresentationRewriteResult>;
  onApply: (slideIndex: number, directive: string) => Promise<PresentationRewriteResult>;
  onError: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const [directive, setDirective] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState<PresentationRewriteResult | null>(null);

  const runPreview = async () => {
    const d = directive.trim();
    if (!d) return;
    setBusy(true);
    onError(null);
    try {
      setProposal(await onPreview(index, d));
    } catch (e) {
      onError(e instanceof Error ? e.message : t("The edit failed."));
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    const d = directive.trim();
    if (!d) return;
    setBusy(true);
    onError(null);
    try {
      await onApply(index, d);
      setProposal(null);
      setDirective("");
    } catch (e) {
      onError(e instanceof Error ? e.message : t("The edit failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      onClick={onSelect}
      className={`rounded-xl border bg-white p-3 ${
        active ? "border-brand-400 ring-1 ring-brand-200" : "border-black/10"
      }`}
    >
      <div className="mb-1 flex items-baseline gap-2">
        <span className="text-xs font-semibold text-ink-faint">{index + 1}</span>
        <h3 className="text-base font-semibold text-ink">
          {slide.title ? <MathText text={slide.title} /> : t("Untitled slide")}
        </h3>
      </div>
      {slide.bullets.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-sm text-ink-soft">
          {slide.bullets.map((b, k) => (
            <li key={k}>
              <MathText text={b} />
            </li>
          ))}
        </ul>
      )}
      {slide.figures.map((q, k) => (
        <span
          key={k}
          className="mt-2 mr-1 inline-block rounded-md bg-zinc-100 px-2 py-0.5 text-xs text-ink-faint"
        >
          {t("Figure")}: {q}
        </span>
      ))}
      {slide.notes.length > 0 && (
        <div className="mt-2 border-t border-black/5 pt-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
            {t("Speaker notes")}
          </p>
          <p className="whitespace-pre-wrap text-xs text-ink-faint">{slide.notes.join("\n")}</p>
        </div>
      )}

      {/* Per-slide AI adjust */}
      <div className="mt-3 border-t border-black/5 pt-2">
        <div className="flex gap-2">
          <input
            value={directive}
            onChange={(e) => setDirective(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={t("Adjust this slide…")}
            className="min-w-0 flex-1 rounded-lg border border-black/10 px-2.5 py-1.5 text-sm"
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || !directive.trim()}
            onClick={(e) => {
              e.stopPropagation();
              void runPreview();
            }}
          >
            {busy && !proposal ? t("Working…") : t("Preview")}
          </Button>
        </div>
        {proposal && (
          <div className="mt-2 rounded-lg bg-zinc-50 p-2">
            {proposal.rationale && (
              <p className="mb-1 text-xs text-ink-soft">{proposal.rationale}</p>
            )}
            {proposal.proposed_markdown != null && (
              <div className="rounded-md border border-black/10 bg-white p-2">
                <MarkdownLite text={proposal.proposed_markdown} />
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  void runApply();
                }}
              >
                {t("Apply")}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  setProposal(null);
                }}
              >
                {t("Discard")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
